import moment from 'moment-timezone';

/**
 * Get page limit based on user context and module type
 * @param {boolean} isLeadSpecific - Whether this is from lead-specific module
 * @param {boolean} isFromCaseSpecificModule - Whether this is from case-specific module
 * @param {Object} userContext - User context from UserDetailsContext
 * @returns {number|undefined} - Page limit preference
 */
export function getPageLimit(isLeadSpecific, isFromCaseSpecificModule, userContext) {
  if (isLeadSpecific && userContext) {
    return userContext?.firmDetails?.pagination_preference?.leads?.billings?.invoices;
  } else if (isFromCaseSpecificModule && userContext) {
    return userContext?.firmDetails?.pagination_preference?.case?.billings?.invoices;
  } else if (userContext) {
    return userContext?.firmDetails?.pagination_preference?.billings?.invoices;
  }
}

/**
 * Get file path for pagination based on module type
 * @param {boolean} isLeadSpecific - Whether this is from lead-specific module
 * @param {boolean} isFromCaseSpecificModule - Whether this is from case-specific module
 * @returns {Object} - Path object with module, subModule, and listName
 */
export function getFilePathForPagination(isLeadSpecific, isFromCaseSpecificModule) {
  if (isLeadSpecific) {
    return { module: 'leads', subModule: 'billings', listName: 'invoices' };
  } else if (isFromCaseSpecificModule) {
    return { module: 'case', subModule: 'billings', listName: 'invoices' };
  } else {
    return { module: 'billings', subModule: '', listName: 'invoices' };
  }
}

/**
 * Extract date range from date array
 * TimezoneDatePicker already returns ISO strings with correct start/end of day times
 * First element has start of day (00:00:00), second element has end of day (23:59:59)
 * @param {Array} dateArray - Array of ISO string dates [startDate, endDate] from TimezoneDatePicker
 * @param {string} timezone - User timezone (used for single date selection handling)
 * @returns {Object} - Object with invoice_date_lower and invoice_date_upper
 */
export function pickExtractedDate(dateArray, timezone) {
  const lowerDate = dateArray?.[0] || '';
  let upperDate = dateArray?.[1] || '';

  // If only one date is selected (upper is null/empty), create end-of-day version for upper bound
  if (lowerDate && !upperDate) {
    const parsed = moment(lowerDate).tz(timezone);
    upperDate = moment
      .tz(
        {
          year: parsed.year(),
          month: parsed.month(),
          date: parsed.date(),
          hour: 23,
          minute: 59,
          second: 59,
        },
        timezone
      )
      .format();
  }

  return {
    invoice_date_lower: lowerDate,
    invoice_date_upper: upperDate,
  };
}
